/**
 * Slice 4.9 — Past Chats expansion content for the company sidebar.
 *
 * Renders inside the "Past chats" expandable nav item. Lazy-loads the
 * recruiter's 10 most recent v2 sessions via davidApiService.listSessions
 * the first time the user expands the item. Each row navigates to
 * /companies/talk-to-david?sessionId=<id>, which TalkToDavid reads via
 * its URL-param effect to open the session in read-only mode (Plan A).
 *
 * Plan A semantics today: clicking a row opens the session as a frozen
 * snapshot. The chat input is disabled until "Start new chat" is clicked.
 *
 * TODO Plan C — when we want "continue this conversation": instead of
 * navigating to ?sessionId=<id>, navigate to a new endpoint that mints a
 * fresh session_id seeded with the past session's selected_* state, and
 * leave the original session frozen in Past Chats.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { davidApiService, SessionSummary } from '../services/davidApi';

interface PastChatsListProps {
    /** When false, skip the fetch — sidebar passes the recruiter id only after auth resolves. */
    enabled?: boolean;
}

const PastChatsList: React.FC<PastChatsListProps> = ({ enabled = true }) => {
    const navigate = useNavigate();
    const [sessions, setSessions] = useState<SessionSummary[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [hasFetched, setHasFetched] = useState(false);

    // Lazy load on first render — Sidebar renders us only when expanded, so
    // the first mount IS the first expansion. Refresh on remount (e.g. user
    // collapses + re-expands) keeps the list reasonably current.
    useEffect(() => {
        if (!enabled || hasFetched) return;
        let cancelled = false;
        (async () => {
            try {
                setLoading(true);
                setError(null);
                const list = await davidApiService.listSessions(10);
                if (!cancelled) {
                    setSessions(list);
                    setHasFetched(true);
                }
            } catch (e: any) {
                if (!cancelled) {
                    console.error('[PastChatsList] fetch failed:', e);
                    setError('Could not load past chats');
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [enabled, hasFetched]);

    if (!enabled) return null;

    if (loading && sessions.length === 0) {
        return <div className="py-2 text-sm text-gray-400 italic">Loading…</div>;
    }
    if (error) {
        return <div className="py-2 text-xs text-red-500">{error}</div>;
    }
    if (sessions.length === 0) {
        return <div className="py-2 text-sm text-gray-400 italic">No past chats yet</div>;
    }

    return (
        <div className="py-1 space-y-0.5">
            {sessions.map((s) => {
                const when = s.updatedAt ? formatTimeAgo(s.updatedAt) : '';
                const preview = s.lastMessagePreview || '(no messages yet)';
                return (
                    <button
                        key={s.sessionId}
                        onClick={() => {
                            navigate(`/companies/talk-to-david?sessionId=${encodeURIComponent(s.sessionId)}`);
                        }}
                        className="w-full text-left px-2 py-1.5 rounded-md text-xs hover:bg-gray-50 transition-colors group"
                        title={preview}
                    >
                        <div className="flex items-center justify-between gap-2 mb-0.5">
                            <span className="text-[10px] uppercase tracking-wide text-gray-400">{when}</span>
                            {s.entryPoint && (
                                <span className="text-[9px] px-1 py-0.5 rounded bg-[#E8EFF6] text-[#015FA6] uppercase">
                                    {labelForEntryPoint(s.entryPoint)}
                                </span>
                            )}
                        </div>
                        <div className="text-gray-600 group-hover:text-[#015FA6] line-clamp-2 leading-snug">
                            {preview}
                        </div>
                    </button>
                );
            })}
        </div>
    );
};

function labelForEntryPoint(ep: string): string {
    switch (ep) {
        case 'find_candidates': return 'Find';
        case 'create_job': return 'Post';
        case 'review_applicant': return 'Review';
        case 'compare_shortlist': return 'Compare';
        default: return ep;
    }
}

function formatTimeAgo(iso: string): string {
    const t = new Date(iso).getTime();
    if (!t) return '';
    const diff = Date.now() - t;
    const minutes = Math.floor(diff / 60_000);
    if (minutes < 1) return 'just now';
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 30) return `${days}d ago`;
    return new Date(iso).toLocaleDateString();
}

export default PastChatsList;
